/**
 * Calendar Event Service
 * Handles generation and formatting of calendar events based on contract types
 */

import React from 'react';
import moment from 'moment';
import { generateUniqueABBR } from '../../../constants';

/**
 * Generate event title for Sum Of contract type
 * @param {Object} item - Calendar item
 * @param {Object} hygData - Hygienist data
 * @param {number} payBillable - Pay billable percentage
 * @param {Object} style - Style object for highlighting
 * @returns {JSX.Element} Event title element
 */
const generateSumOfEventTitle = (item, hygData, payBillable, style) => {
  if (item.type === "basePay") {
    return (
      <div className="eventTitle">
        <span>Base Pay :</span> <span style={style}>{parseFloat(item.price).toFixed(2)}</span>
      </div>
    );
  }
  
  if (item.type === "bonusPay") {
    return (
      <div className="eventTitle">
        <span> Bonus Pay :</span> <span style={style}>{parseFloat(item.price).toFixed(2)}</span>
      </div>
    );
  }
  
  if (item.type === "totalPay") {
    if (Object.keys(hygData).length > 0) {
      const date = item.id.split("total-")[1];
      const practice = item.practice;
      
      if (hygData[date] && hygData[date].practice === practice) {
        return (
          <div className="eventTitle">
            Total Pay : <span style={style}>{parseFloat(item.price || '').toFixed(2)}</span>
            <br />
            Pay/Hyn: <span>{(hygData[date].billableCare * (payBillable / 100)).toFixed(2)}</span>
          </div>
        );
      }
    }
    
    return (
      <div className="eventTitle">
        <span> Total Pay :</span> <span style={style}>{parseFloat(item.price || '').toFixed(2)}</span>
      </div>
    );
  }
  
  if (item.type === "financialNote") {
    return (
      <div className="eventTitle">
        <span>Fin Note :</span> <span style={style}>{parseFloat(item.price).toFixed(2)}</span>
      </div>
    );
  }
  
  return item.title;
};

/**
 * Generate event title for Greater Of contract types (Greater Of, GOX, GOZ, Greater Of w/HYN, GOX w/HYN)
 * @param {Object} item - Calendar item
 * @param {Object} hygData - Hygienist data
 * @param {number} payBillable - Pay billable percentage
 * @param {Object} style - Style object for highlighting
 * @returns {JSX.Element} Event title element
 */
const generateGreaterOfEventTitle = (item, hygData, payBillable, style) => {
  if (item.type === "hynBillableCare") {
    return (
      <div className="eventTitle">
        <span>Hyg bCare :</span> <span style={style}>{parseFloat(item.price).toFixed(2)}</span>
      </div>
    );
  }
  
  if (item.type === "totalPay") {
    if (Object.keys(hygData).length > 0) {
      const date = item.id.split("total-")[1];
      const practice = item.practice;
      
      if (hygData[date] && hygData[date].practice === practice) {
        return (
          <div>
            Pay : <span style={style}>{parseFloat(item.price || '').toFixed(2)}</span>
            <br />
            Pay/Hyn: <span>{(hygData[date].billableCare * (payBillable / 100)).toFixed(2)}</span>
          </div>
        );
      }
    }
    
    return (
      <div>
        Pay : <span style={style}>{parseFloat(item.price || '').toFixed(2)}</span>
      </div>
    );
  }
  
  if (item.type === "financialNote") {
    return (
      <div className="eventTitle">
        <span>Fin Note :</span> <span style={style}>{parseFloat(item.price).toFixed(2)}</span>
      </div>
    );
  }
  
  return item.title;
};

/**
 * Generate event title for UKG HYN contract type
 * @param {Object} item - Calendar item
 * @param {Object} style - Style object for highlighting
 * @returns {JSX.Element} Event title element
 */
const generateUkgHynEventTitle = (item, style) => {
  if (item.type === "production") {
    return (
      <div>
        bCare : <span style={style}>{parseFloat((item.price || '').replace(',', '')).toFixed(2)}</span>
      </div>
    );
  }
  
  if (item.type === "financialNote") {
    return (
      <div className="eventTitle">
        <span>Fin Note :</span> <span style={style}>{parseFloat(item.price).toFixed(2)}</span>
      </div>
    );
  }
  
  return item.title;
};

/**
 * Generate event title for Collections contract type
 * @param {Object} item - Calendar item
 * @param {Object} hygData - Hygienist data
 * @param {Object} style - Style object for highlighting
 * @returns {JSX.Element} Event title element
 */
const generateCollectionsEventTitle = (item, hygData, style) => {
  if (item.type === "collection") {
    if (Object.keys(hygData).length > 0) {
      const date = item.id.split("collection-")[1];
      const practice = item.practice;
      const hygKeys = Object.keys(hygData);
      const hygAbbr = generateUniqueABBR(hygKeys);
      
      let sumOfHynCollections = 0;
      hygAbbr.forEach((abbr) => {
        const currHygKey = abbr + "_" + date;
        sumOfHynCollections += hygData[currHygKey] && hygData[currHygKey].practice === practice
          ? hygData[currHygKey].Amount
          : 0;
      });
      
      if (sumOfHynCollections !== 0) {
        return (
          <div>
            Collection : <span style={style}>{item.price.toFixed(2)}</span>
            <br />
            Collection/Hyn: {sumOfHynCollections.toFixed(2)}
          </div>
        );
      }
    }
    
    return <div>Collection : {item.price.toFixed(2)}</div>;
  }
  
  if (item.type === "financialNote") {
    return (
      <div className="eventTitle">
        <span>Fin Note :</span> <span style={style}>{parseFloat(item.price).toFixed(2)}</span>
      </div>
    );
  }
  
  if (item.type === "totalPay") {
    return (
      <div className="eventTitle">
        <span> Total Pay :</span> <span style={style}>{parseFloat(item.price).toFixed(2)}</span>
      </div>
    );
  }
  
  return item.title;
};

/**
 * Generate event title for Prod Hurdle contract type
 * @param {Object} item - Calendar item
 * @param {Object} style - Style object for highlighting
 * @returns {JSX.Element} Event title element
 */
const generateProdHurdleEventTitle = (item, style) => {
  if (item.type === "basePay") {
    return (
      <div className="eventTitle">
        <span>Base Pay :</span> <span style={style}>{parseFloat(item.price).toFixed(2)}</span>
      </div>
    );
  }
  
  if (item.type === "bonusPay") {
    return (
      <div className="eventTitle">
        <span> Bonus Pay :</span> <span style={style}>{parseFloat(item.price).toFixed(2)}</span>
      </div>
    );
  }
  
  if (item.type === "totalPay") {
    return (
      <div className="eventTitle">
        <span> Total Pay :</span> <span style={style}>{parseFloat(item.price).toFixed(2)}</span>
      </div>
    );
  }
  
  if (item.type === "financialNote") {
    return (
      <div className="eventTitle">
        <span>Fin Note :</span> <span style={style}>{parseFloat(item.price).toFixed(2)}</span>
      </div>
    );
  }
  
  return item.title;
};

/**
 * Generate event title based on contract type
 * @param {Object} item - Calendar item
 * @param {string} contractType - Contract type name
 * @param {Object} hygData - Hygienist data
 * @param {number} payBillable - Pay billable percentage
 * @returns {JSX.Element} Event title element
 */
export const generateEventTitle = (item, contractType, hygData, payBillable) => {
  const style = item.hasProviderWorkedInTwoPracticesToday 
    ? { background: 'primary', color: '#d67d38' } 
    : {};
  
  // Sum Of contract types
  if (contractType === 'Sum Of' || contractType === 'Sum Of w/HYN') {
    return generateSumOfEventTitle(item, hygData, payBillable, style);
  }
  
  // Greater Of contract types
  if (
    contractType === 'Greater Of' ||
    contractType === 'GOX' ||
    contractType === 'GOZ' ||
    contractType === 'Greater Of w/HYN' ||
    contractType === 'GOX w/HYN' ||
    contractType === 'GO GA' ||
    contractType === 'GO GA w/HYN'
  ) {
    return generateGreaterOfEventTitle(item, hygData, payBillable, style);
  }
  
  // UKG HYN contract
  if (contractType === 'UKG HYN') {
    return generateUkgHynEventTitle(item, style);
  }
  
  // Collections contract types
  if (contractType === 'Collections' || contractType === 'Collections w/HYN') {
    return generateCollectionsEventTitle(item, hygData, style);
  }
  
  // Prod Hurdle contract
  if (contractType === 'Prod Hurdle') {
    return generateProdHurdleEventTitle(item, style);
  }
  
  return item.title;
};

/**
 * Format a single calendar event item
 * @param {Object} item - Calendar item to format
 * @param {string} contractType - Contract type name
 * @param {Object} hygData - Hygienist data
 * @param {number} payBillable - Pay billable percentage
 * @returns {Object} Formatted calendar item
 */
export const formatCalendarEvent = (item, contractType, hygData, payBillable) => {
  const formattedItem = { ...item };
  
  // Generate title based on contract type
  formattedItem.title = generateEventTitle(item, contractType, hygData, payBillable);
  
  // Convert date strings to Date objects for react-big-calendar
  formattedItem.start = moment(item.start).toDate();
  formattedItem.end = moment(item.end).toDate();
  
  return formattedItem;
};

/**
 * Generate complete calendar pay list with formatted events
 * @param {Array} calendarPayList - Raw calendar pay list
 * @param {string} contractType - Contract type name
 * @param {Object} hygData - Hygienist data (date-keyed object)
 * @param {number} payBillable - Pay billable percentage
 * @returns {Array} Formatted calendar events
 */
export const generateCalendarPayList = (calendarPayList, contractType, hygData = {}, payBillable = 0) => {
  if (!contractType || !Array.isArray(calendarPayList)) {
    return [];
  }
  
  return calendarPayList.map(item => formatCalendarEvent(item, contractType, hygData, payBillable));
};
